// frontend/src/operator/OperatorDashboard.js
// 運営者ダッシュボード：組織一覧 + 有効/無効トグル + 招待発行 + 招待一覧

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

const OPERATOR_PATH = process.env.REACT_APP_OPERATOR_PATH || 'ops-7k3m9x2p4r8q';
const SUPABASE_URL = 'https://tpxzotpgkcaioifgzlii.supabase.co';

export default function OperatorDashboard() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  // Organizations
  const [orgs, setOrgs] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgError, setOrgError] = useState('');
  const [togglingId, setTogglingId] = useState(null);

  // Invites
  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState('');

  // Create invite modal
  //   initial: null              → 新規病院+新規部署 (= 現状動作)
  //   initial: { hospitalId, hospitalName } → 既存病院に部署追加モード
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitial, setCreateInitial] = useState(null);
  const [issuedInvite, setIssuedInvite] = useState(null);

  const callApi = async (path, body) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error('セッション切れ');

    const res = await fetch(`${SUPABASE_URL}/functions/v1/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : '{}',
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'リクエスト失敗');
    return json;
  };

  const loadOrganizations = useCallback(async () => {
    setOrgLoading(true);
    setOrgError('');
    try {
      const { organizations, hospitals: hospitalList } = await callApi('admin-list-organizations');
      setOrgs(organizations || []);
      setHospitals(hospitalList || []);
    } catch (e) {
      setOrgError(e.message);
    } finally {
      setOrgLoading(false);
    }
  }, []);

  const loadInvites = useCallback(async () => {
    setInviteLoading(true);
    setInviteError('');
    try {
      const { invites } = await callApi('admin-list-invites');
      setInvites(invites || []);
    } catch (e) {
      setInviteError(e.message);
    } finally {
      setInviteLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data?.user?.email || ''));
    loadOrganizations();
    loadInvites();
  }, [loadOrganizations, loadInvites]);

  const handleToggle = async (org) => {
    const next = !org.is_active;
    const action = next ? '有効化' : '無効化';
    if (!window.confirm(`「${org.name}」を${action}しますか？\n\n${
      next
        ? '所属メンバーがログインできるようになります。'
        : '無効化すると、所属メンバーは即座にログイン不可・操作不可になります。'
    }`)) return;

    setTogglingId(org.id);
    try {
      await callApi('admin-toggle-organization', {
        organizationId: org.id,
        isActive: next,
      });
      setOrgs((prev) =>
        prev.map((o) => (o.id === org.id ? { ...o, is_active: next } : o))
      );
    } catch (e) {
      alert(`${action}に失敗: ${e.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreateInvite = async (formData) => {
    const baseUrl = window.location.origin;
    // formData は { orgSlug, orgName, adminEmail, plan, hospitalId? }
    const json = await callApi('admin-create-invite', { ...formData, baseUrl });
    setIssuedInvite(json.invite);
    await loadInvites();
    await loadOrganizations();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate(`/${OPERATOR_PATH}/login`, { replace: true });
  };

  const inviteStatus = (inv) => {
    if (inv.accepted_at) return { label: '受諾済み', style: styles.badgeAccepted };
    if (new Date(inv.expires_at).getTime() < Date.now()) return { label: '期限切れ', style: styles.badgeExpired };
    return { label: '有効', style: styles.badgePending };
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>ScrubEdge 運営パネル</h1>
        <div style={styles.userBox}>
          <span style={styles.email}>{email}</span>
          <button
            onClick={() => navigate(`/${OPERATOR_PATH}/data-formats`)}
            style={styles.navBtn}
          >📦 データフォーマット</button>
          <button
            onClick={() => navigate(`/${OPERATOR_PATH}/audit-logs`)}
            style={styles.navBtn}
          >🔍 監査ログ</button>
          <button
            onClick={() => navigate(`/${OPERATOR_PATH}/users`)}
            style={styles.navBtn}
          >🔐 運営ユーザー</button>
          <button
            onClick={() => navigate(`/${OPERATOR_PATH}/password`)}
            style={styles.navBtn}
          >🔑 パスワード変更</button>
          <button onClick={handleLogout} style={styles.logoutBtn}>ログアウト</button>
        </div>
      </header>

      <main style={styles.main}>

        {/* 病院別の組織一覧 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>病院 × 部署一覧</h2>
            <button onClick={loadOrganizations} style={styles.reloadBtn}>↻ 再読み込み</button>
          </div>

          {orgLoading && <div style={styles.message}>読み込み中...</div>}
          {orgError && <div style={styles.errorBox}>エラー: {orgError}</div>}

          {!orgLoading && !orgError && orgs.length === 0 && (
            <div style={styles.message}>組織がまだありません。</div>
          )}

          {!orgLoading && !orgError && orgs.length > 0 && (() => {
            // hospital_id ごとに orgs をグループ化
            const byHospital = new Map();
            orgs.forEach((o) => {
              const key = o.hospital_id || '__orphan__';
              if (!byHospital.has(key)) byHospital.set(key, []);
              byHospital.get(key).push(o);
            });
            const hospitalById = new Map(hospitals.map((h) => [h.id, h]));

            // 既知の hospital で、まだ 1 件も org が無いもの (hospital_id 単独で存在) も出す
            hospitals.forEach((h) => {
              if (!byHospital.has(h.id)) byHospital.set(h.id, []);
            });

            // 配列化してソート: 病院名の昇順、orphan は最後
            const groups = Array.from(byHospital.entries())
              .map(([hospitalId, groupOrgs]) => ({
                hospitalId,
                hospital: hospitalById.get(hospitalId) || null,
                orgs: groupOrgs,
              }))
              .sort((a, b) => {
                if (a.hospitalId === '__orphan__') return 1;
                if (b.hospitalId === '__orphan__') return -1;
                return (a.hospital?.name || '').localeCompare(b.hospital?.name || '');
              });

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {groups.map(({ hospitalId, hospital, orgs: groupOrgs }) => (
                  <div key={hospitalId} style={styles.hospitalGroup}>
                    <div style={styles.hospitalGroupHeader}>
                      <div>
                        <div style={styles.hospitalName}>
                          🏥 {hospital?.name || (hospitalId === '__orphan__' ? '(病院未紐付け)' : hospitalId)}
                        </div>
                        {hospital?.id && (
                          <div style={styles.hospitalMeta}>部署 {groupOrgs.length} 件</div>
                        )}
                      </div>
                      {hospitalId !== '__orphan__' && hospital?.id && (
                        <button
                          onClick={() => {
                            setIssuedInvite(null);
                            setCreateInitial({
                              hospitalId: hospital.id,
                              hospitalName: hospital.name,
                            });
                            setCreateOpen(true);
                          }}
                          style={styles.btnSecondary}
                        >
                          + この病院に部署を追加
                        </button>
                      )}
                    </div>

                    {groupOrgs.length === 0 ? (
                      <div style={styles.emptyGroup}>この病院にはまだ部署が登録されていません。</div>
                    ) : (
                      <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                          <thead>
                            <tr style={styles.tr}>
                              <th style={styles.th}>状態</th>
                              <th style={styles.th}>部署名</th>
                              <th style={styles.th}>スラッグ</th>
                              <th style={styles.th}>プラン</th>
                              <th style={styles.th}>メンバー数</th>
                              <th style={styles.th}>作成日</th>
                              <th style={styles.thRight}>操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupOrgs.map((org) => (
                              <tr key={org.id} style={styles.tr}>
                                <td style={styles.td}>
                                  <span style={org.is_active ? styles.badgeActive : styles.badgeInactive}>
                                    {org.is_active ? '有効' : '無効'}
                                  </span>
                                </td>
                                <td style={styles.td}>{org.name}</td>
                                <td style={styles.tdMono}>{org.id}</td>
                                <td style={styles.td}>{org.plan || 'free'}</td>
                                <td style={styles.tdNum}>{org.member_count}</td>
                                <td style={styles.td}>
                                  {org.created_at ? new Date(org.created_at).toLocaleDateString('ja-JP') : '-'}
                                </td>
                                <td style={styles.tdRight}>
                                  <button
                                    onClick={() => handleToggle(org)}
                                    disabled={togglingId === org.id}
                                    style={
                                      org.is_active
                                        ? { ...styles.btnDanger, ...(togglingId === org.id ? styles.btnDisabled : {}) }
                                        : { ...styles.btnPrimary, ...(togglingId === org.id ? styles.btnDisabled : {}) }
                                    }
                                  >
                                    {togglingId === org.id ? '...' : (org.is_active ? '無効化' : '有効化')}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </section>

        {/* 招待管理 */}
        <section style={{ ...styles.section, marginTop: '1.5rem' }}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>招待管理</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={loadInvites} style={styles.reloadBtn}>↻ 再読み込み</button>
              <button onClick={() => { setIssuedInvite(null); setCreateInitial(null); setCreateOpen(true); }} style={styles.btnPrimary}>
                + 新規病院+部署を発行
              </button>
            </div>
          </div>

          {inviteLoading && <div style={styles.message}>読み込み中...</div>}
          {inviteError && <div style={styles.errorBox}>エラー: {inviteError}</div>}

          {!inviteLoading && !inviteError && invites.length === 0 && (
            <div style={styles.message}>招待はまだありません。</div>
          )}

          {!inviteLoading && !inviteError && invites.length > 0 && (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tr}>
                    <th style={styles.th}>状態</th>
                    <th style={styles.th}>組織名</th>
                    <th style={styles.th}>スラッグ</th>
                    <th style={styles.th}>管理者メール</th>
                    <th style={styles.th}>プラン</th>
                    <th style={styles.th}>有効期限</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => {
                    const status = inviteStatus(inv);
                    return (
                      <tr key={inv.id} style={styles.tr}>
                        <td style={styles.td}><span style={status.style}>{status.label}</span></td>
                        <td style={styles.td}>{inv.intended_org_name}</td>
                        <td style={styles.tdMono}>{inv.intended_org_slug}</td>
                        <td style={styles.td}>{inv.intended_admin_email}</td>
                        <td style={styles.td}>{inv.intended_plan}</td>
                        <td style={styles.td}>
                          {new Date(inv.expires_at).toLocaleDateString('ja-JP')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {createOpen && (
        <CreateInviteModal
          onClose={() => { setCreateOpen(false); setCreateInitial(null); }}
          onSubmit={handleCreateInvite}
          issuedInvite={issuedInvite}
          initial={createInitial}
        />
      )}
    </div>
  );
}

// ============================================================================
// CreateInviteModal
// ============================================================================
function CreateInviteModal({ onClose, onSubmit, issuedInvite, initial }) {
  // initial = { hospitalId, hospitalName } で呼ばれると「既存病院に部署追加」モード
  const isDepartmentMode = !!initial?.hospitalId;
  const [hospitalName, setHospitalName] = useState('');  // 新規病院モードでのみ使用
  const [orgSlug, setOrgSlug] = useState('');
  const [orgName, setOrgName] = useState('');            // 部署名
  const [adminEmail, setAdminEmail] = useState('');
  const [plan, setPlan] = useState('free');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isDepartmentMode && !hospitalName.trim()) {
      setError('病院名を入力してください');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        orgSlug,
        orgName,                                            // 部署名
        hospitalName: isDepartmentMode ? null : hospitalName.trim(),
        adminEmail,
        plan,
        hospitalId: initial?.hospitalId || null,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(issuedInvite.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div style={modalStyles.card} onClick={(e) => e.stopPropagation()}>
        {issuedInvite ? (
          // ============ 発行完了画面 ============
          <>
            <h2 style={modalStyles.title}>招待を発行しました</h2>
            <p style={modalStyles.helper}>
              以下の URL をクライアント病院の管理者にメール等で送付してください。
            </p>

            <div style={modalStyles.urlBox}>
              <code style={modalStyles.urlText}>{issuedInvite.url}</code>
            </div>

            <button onClick={copyUrl} style={modalStyles.copyBtn}>
              {copied ? '✓ コピーしました' : 'URL をコピー'}
            </button>

            <div style={modalStyles.detailBox}>
              {issuedInvite.intended_hospital_name && (
                <div><strong>病院名:</strong> {issuedInvite.intended_hospital_name}</div>
              )}
              <div><strong>部署名:</strong> {issuedInvite.intended_org_name}</div>
              <div><strong>スラッグ:</strong> {issuedInvite.intended_org_slug}</div>
              <div><strong>管理者メール:</strong> {issuedInvite.intended_admin_email}</div>
              <div><strong>プラン:</strong> {issuedInvite.intended_plan}</div>
              <div><strong>有効期限:</strong> {new Date(issuedInvite.expires_at).toLocaleString('ja-JP')}</div>
            </div>

            <button onClick={onClose} style={modalStyles.closeBtn}>閉じる</button>
          </>
        ) : (
          // ============ 入力フォーム ============
          <>
            <h2 style={modalStyles.title}>
              {isDepartmentMode ? '部署を追加' : '新規病院+部署の招待を発行'}
            </h2>
            <p style={modalStyles.helper}>
              {isDepartmentMode
                ? `既存病院「${initial.hospitalName}」の配下に新しい部署を追加する招待 URL を発行します。`
                : '新規病院とその最初の部署を登録するための招待 URL を発行します。発行内容は受諾時の組織情報になります。'}
            </p>

            {isDepartmentMode && (
              <div style={modalStyles.hospitalBanner}>
                🏥 {initial.hospitalName}
              </div>
            )}

            {error && <div style={modalStyles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              {!isDepartmentMode && (
                <>
                  <label style={modalStyles.label}>病院名 *</label>
                  <input
                    type="text"
                    value={hospitalName}
                    onChange={(e) => setHospitalName(e.target.value)}
                    required
                    placeholder="例: ◯◯医科大学病院"
                    style={modalStyles.input}
                  />
                  <p style={modalStyles.fieldHelp}>
                    病院マスタ (hospitals.name) に登録される値。同病院に別部署を追加する場合は共通になります。
                  </p>
                </>
              )}

              <label style={modalStyles.label}>部署名 *</label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                placeholder={isDepartmentMode ? '例: 中央手術部 / 外来 / 心臓外科' : '例: 中央手術部 (最初の部署)'}
                style={modalStyles.input}
              />

              <label style={modalStyles.label}>スラッグ（部署の組織ID、ログインで使用） *</label>
              <input
                type="text"
                value={orgSlug}
                onChange={(e) => setOrgSlug(e.target.value.toLowerCase())}
                required
                placeholder={isDepartmentMode ? '例: marianna-ope / marianna-ope-2' : '例: marianna-ope (病院共通で分かるものを)'}
                pattern="[a-z0-9][a-z0-9-]{1,48}[a-z0-9]"
                title="小文字英数字とハイフンのみ、3-50 文字"
                style={{ ...modalStyles.input, fontFamily: 'monospace' }}
              />
              <p style={modalStyles.fieldHelp}>
                小文字英数字とハイフンのみ。3-50 文字。この部署にログインする際に使われます (= organizations.id)。
              </p>

              <label style={modalStyles.label}>管理者メール *</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                placeholder="admin@example.com"
                style={modalStyles.input}
              />

              <label style={modalStyles.label}>プラン</label>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                style={modalStyles.input}
              >
                <option value="free">free</option>
                <option value="pro">pro</option>
                <option value="enterprise">enterprise</option>
              </select>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button type="button" onClick={onClose} style={modalStyles.cancelBtn}>キャンセル</button>
                <button type="submit" disabled={submitting} style={modalStyles.submitBtn}>
                  {submitting ? '発行中...' : '発行する'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================
const styles = {
  page: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    background: '#0f172a',
    color: '#fff',
    padding: '1rem 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: { fontSize: '1.25rem', margin: 0, fontWeight: 700 },
  userBox: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  email: { fontSize: '0.85rem', color: '#cbd5e1', marginRight: '0.5rem' },
  navBtn: {
    background: '#334155',
    border: 'none',
    color: '#e2e8f0',
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  logoutBtn: {
    background: 'transparent',
    border: '1px solid #475569',
    color: '#fff',
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
  },
  section: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  sectionTitle: { fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#0f172a' },
  reloadBtn: {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    padding: '0.4rem 0.9rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
    color: '#334155',
  },
  message: { padding: '2rem', textAlign: 'center', color: '#64748b' },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    padding: '0.85rem 1rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
  },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  th: {
    textAlign: 'left',
    padding: '0.75rem 0.5rem',
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  thRight: { textAlign: 'right', padding: '0.75rem 0.5rem' },
  td: { padding: '0.85rem 0.5rem', color: '#0f172a' },
  tdNum: { padding: '0.85rem 0.5rem', color: '#0f172a', fontVariantNumeric: 'tabular-nums' },
  tdMono: {
    padding: '0.85rem 0.5rem',
    color: '#475569',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '0.85rem',
  },
  tdRight: { padding: '0.85rem 0.5rem', textAlign: 'right' },
  badgeActive: { background: '#dcfce7', color: '#166534', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 },
  badgeInactive: { background: '#fee2e2', color: '#991b1b', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 },
  badgePending: { background: '#dbeafe', color: '#1e40af', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 },
  badgeAccepted: { background: '#dcfce7', color: '#166534', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 },
  badgeExpired: { background: '#f1f5f9', color: '#475569', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 },
  btnPrimary: {
    background: '#0f172a',
    color: '#fff',
    border: 'none',
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  btnDanger: {
    background: '#fff',
    color: '#b91c1c',
    border: '1px solid #fca5a5',
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  btnSecondary: {
    background: '#eef2ff',
    color: '#4338ca',
    border: '1px dashed #6366f1',
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  hospitalGroup: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#f8fafc',
  },
  hospitalGroupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.85rem 1rem',
    background: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  hospitalName: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#0f172a',
  },
  hospitalMeta: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: 2,
  },
  emptyGroup: {
    padding: '1rem',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '0.85rem',
  },
};

const modalStyles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  title: { fontSize: '1.2rem', fontWeight: 700, color: '#0f172a', margin: '0 0 0.5rem 0' },
  helper: { fontSize: '0.9rem', color: '#475569', margin: '0 0 1.5rem 0', lineHeight: 1.6 },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.4rem', marginTop: '1rem' },
  input: {
    width: '100%',
    padding: '0.7rem 0.9rem',
    fontSize: '0.95rem',
    border: '1.5px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  fieldHelp: { fontSize: '0.8rem', color: '#64748b', margin: '0.4rem 0 0 0' },
  cancelBtn: {
    flex: 1,
    padding: '0.7rem',
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    color: '#334155',
  },
  submitBtn: {
    flex: 2,
    padding: '0.7rem',
    background: '#0f172a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    padding: '0.7rem 1rem',
    borderRadius: '8px',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  urlBox: {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '0.75rem',
    overflowX: 'auto',
  },
  urlText: {
    fontFamily: 'ui-monospace, monospace',
    fontSize: '0.85rem',
    color: '#0f172a',
    wordBreak: 'break-all',
  },
  copyBtn: {
    width: '100%',
    padding: '0.7rem',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: '1.5rem',
  },
  detailBox: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '1rem',
    fontSize: '0.85rem',
    lineHeight: 1.8,
    color: '#334155',
    marginBottom: '1.5rem',
  },
  closeBtn: {
    width: '100%',
    padding: '0.7rem',
    background: '#fff',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  hospitalBanner: {
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    color: '#4338ca',
    padding: '0.5rem 0.9rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: '1rem',
  },
};
